import { useState, type ReactNode } from "react";
import { useSession } from "../store/session.js";
import { useRouter, Link } from "../lib/router.js";
import { api, ApiError } from "../lib/api.js";
import { Button, Screen } from "../components/ui.js";
import { ChevronLeftIcon, GlobeIcon, LockIcon } from "../components/icons.js";

export function Create() {
  const { token } = useSession();
  const { navigate } = useRouter();
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!token) {
      navigate("/");
      return;
    }
    if (visibility === "private" && !/^\d{4}$/.test(pin)) {
      setError("Enter a 4-digit PIN.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createGame(
        { visibility, pin: visibility === "private" ? pin : undefined },
        token,
      );
      navigate(`/g/${res.id}`);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === "rate_limited"
          ? "You're creating games too fast — wait a minute and try again."
          : "Couldn't create the game. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen className="py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm font-bold opacity-60">
        <ChevronLeftIcon className="h-4 w-4" /> Back
      </Link>
      <h1 className="mb-4 text-2xl font-black">New game</h1>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <VisCard
          active={visibility === "public"}
          onClick={() => setVisibility("public")}
          title="Public"
          icon={<GlobeIcon className="h-8 w-8" />}
          desc="Anyone can find and join from the lobby."
        />
        <VisCard
          active={visibility === "private"}
          onClick={() => setVisibility("private")}
          title="Private"
          icon={<LockIcon className="h-8 w-8" />}
          desc="Share a link or code + PIN to invite one friend."
        />
      </div>

      {visibility === "private" && (
        <div className="mb-4">
          <label
            htmlFor="create-pin"
            className="mb-1 block px-1 text-xs font-bold uppercase tracking-wide opacity-60"
          >
            4-digit PIN
          </label>
          <input
            id="create-pin"
            name="pin"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            enterKeyHint="done"
            autoComplete="off"
            aria-label="4-digit game PIN"
            placeholder="••••"
            className="clay-inset w-full bg-transparent px-4 py-3 text-center text-2xl font-black tracking-[0.5em] outline-none"
          />
        </div>
      )}

      {error && <p className="mb-3 text-sm font-bold text-red-500">{error}</p>}

      <Button variant="primary" disabled={busy} onClick={create}>
        {busy ? "Creating…" : "Create & get link"}
      </Button>
    </Screen>
  );
}

function VisCard({
  active,
  onClick,
  title,
  icon,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  icon: ReactNode;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`clay-btn flex flex-col items-start p-3 text-left ${active ? "ring-2 ring-amber-400" : ""}`}
    >
      {icon}
      <span className="mt-1 font-black">{title}</span>
      <span className="mt-0.5 text-xs opacity-60">{desc}</span>
    </button>
  );
}
